import VacancyApplicationItem from './VacancyApplicationItem'
import VacancyLocation from './VacancyLocation'
import VacancyRecruiter from './VacancyRecruiter'
import cloneDeep from 'lodash/cloneDeep'
import get from 'lodash/get'
import omit from 'lodash/omit'
import unescape from 'lodash/unescape'
import { ModerationStages } from '@/constants/backend/ModerationStages'
import { WorkExperience } from '@/constants/vacancies/experiences'
import { PositionType } from '@/constants/vacancies/positionTypes'
import { SalaryTypes } from '@/constants/vacancies/SalaryTypes'
import { Stages, Statuses } from '@/constants/vacancies/statuses'
import Skill from '@/models-ts/Skill'
import FreelancerListItem from '@/models-ts/user/FreelancerListItem'
import VacancyRole from '@/models-ts/vacancies/VacancyRole'
import { parseSlug } from '@/utils-ts/parser'

export default class Vacancy {
  id: number | null
  slug: string | null
  status: Statuses | null
  stage: Stages | null
  customer_id: number | null
  name: string
  description: string
  position_office: boolean
  position_remote: boolean
  position_type: PositionType | null
  moderation_stage?: ModerationStages | null
  payments_is_crypto: boolean
  salary_type: number
  salary_from: number
  salary_to: number
  work_experience: WorkExperience | null
  primaryRole: VacancyRole['id'] | null
  primaryRoleName: VacancyRole['name'] | null
  external_url: string | null
  applicationForm: Array<ApplicationFormField>
  applicationFormEnabled: boolean
  isCvMatchFeedbackSent: boolean
  relations: {
    Skill: Array<Skill>
    Location: Array<VacancyLocation>
    Offer: Array<{}>
    Customer: VacancyRecruiter | null
  }
  applications: Array<VacancyApplicationItem>
  bookmarkId?: number | null
  category?: Skill | null
  relatedFreelancers: Array<FreelancerListItem> | null

  constructor(data: Partial<Vacancy> | void) {
    Object.assign(
      this,
      cloneDeep({
        id: null,
        slug: null,
        status: null,
        stage: null,
        customer_id: null,
        name: '',
        description: '',
        position_office: false,
        position_remote: false,
        position_type: null,
        payments_is_crypto: false,
        salary_type: SalaryTypes.RANGE,
        salary_from: 0,
        salary_to: 0,
        work_experience: WorkExperience.NOT_SET,
        primaryRole: null,
        primaryRoleName: null,
        external_url: null,
        applications: [],
        relations: {
          Skill: [],
          Location: [],
          Offer: [],
          Customer: null,
        },
        ...data,
      })
    )
  }

  static fromServer({ additional_applications_form, additional_applications_form_is_enabled, ...data }: LX.Vacancy) {
    const primaryRole = get(data.relations, 'PrimaryRole[0]', { id: null, name: null })
    return new Vacancy({
      ...data,
      name: unescape(data.name),
      slug: parseSlug(data.slug),
      position_office: Boolean(data.position_office),
      position_remote: Boolean(data.position_remote),
      payments_is_crypto: Boolean(data.payments_is_crypto),
      salary_from: Number(data.salary_from || 0),
      salary_to: Number(data.salary_to || 0),
      primaryRole: primaryRole.id,
      primaryRoleName: primaryRole.name,
      external_url: data.external_url,
      applicationForm: additional_applications_form || [],
      applicationFormEnabled: additional_applications_form_is_enabled,
      isCvMatchFeedbackSent: Boolean(data.is_cv_match_feedback_sent),
      relations: {
        Customer: VacancyRecruiter.fromServer(data.relations.Customer),
        Skill: (data.relations.Skill || []).map(Skill.fromServer),
        Location: (data.relations.Location || []).map(VacancyLocation.fromServer),
        Offer: data.relations.Offer || [],
      },
      bookmarkId: get(data, 'meta.bookmarks[0].id', null),
      category: data.meta?.category && Skill.fromServer(data.meta?.category),
      applications: (data.relations.Application || []).map(VacancyApplicationItem.fromServer),
      relatedFreelancers: (data.meta?.related?.freelancers || []).map(FreelancerListItem.fromServer),
    })
  }

  toServer() {
    const { primaryRole, payments_is_crypto, position_office, position_remote, salary_to, status, ...payload } = omit(this, 'country_id')
    return {
      ...payload,
      payments_is_crypto: Number(payments_is_crypto),
      position_office: Number(position_office),
      position_remote: Number(position_remote),
      salary_to: this.salary_type === SalaryTypes.FIXED ? this.salary_from : this.salary_to,
      relations: {
        Location: payload.relations.Location,
        Skill: payload.relations.Skill.map(({ id }) => ({ id })),
        PrimaryRole: primaryRole ? [{ id: primaryRole }] : [],
      },
    }
  }

  get hasCompensation() {
    return this.salary_from > 0 || this.salary_to > 0
  }

  get remoteInfo() {
    if (this.position_remote) {
      return this.position_office ? 'Hybrid' : 'Remote'
    }
    return 'Office'
  }
}

export interface ApplicationFormField {
  group: string | null
  technical: {
    uuid: string
  }
  label?: string | null
  option?: { title: string; value: string } | null
  options?: Array<{ title: string; value: string }> | null
  type: 'hidden' | 'input' | 'select' | 'checkbox' | 'textarea' | 'section' | 'radio' | 'file_upload'
  validations?: {
    maxlength?: number | null
    required?: boolean
  }
  value?: string | null
  addons?: ApplicationFormField | null
}
